import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
    Card,
    CardBody,
    CardHeader,
    CardFooter,
    Button,
    Autocomplete,
    AutocompleteItem,
    DatePicker,
    NumberInput,
    Textarea,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Chip,
} from "@heroui/react";
import { LuArrowLeft, LuPlus, LuTrash2, LuSave } from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { usePostData, useFetchData } from "@/hooks";
import { showToast, findOptionTitle } from "@/utils";
import { ThemedText, PageHeader } from "@/components/ui";
import { parseDate, getLocalTimeZone, today } from "@internationalized/date";
import { BASE_API } from "@/constants";

const emptyItem = {
    material_id: "",
    warehouse_id: "",
    material_unit_id: "",
    date: null,
    qty_out: null,
    description: "",
};

const StockOutCreate = () => {
    const navigate = useNavigate();
    const [unitsPerIndex, setUnitsPerIndex] = useState({});
    const [balancesPerIndex, setBalancesPerIndex] = useState({});
    const [pendingData, setPendingData] = useState(null);
    const confirmModal = useDisclosure();

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            items: [{ ...emptyItem }],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "items",
    });

    const watchItems = watch("items");

    const { data: materials, isLoading: isLoadingMaterials } =
        useFetchData("select/material");
    const { data: warehouses, isLoading: isLoadingWarehouses } =
        useFetchData("select/warehouse");

    const storeMutation = usePostData("stock-out/store");

    const fetchUnitsForIndex = async (index, materialId) => {
        if (!materialId) {
            setUnitsPerIndex((prev) => ({
                ...prev,
                [index]: { data: [], isLoading: false },
            }));
            return;
        }

        setUnitsPerIndex((prev) => ({
            ...prev,
            [index]: { data: prev[index]?.data || [], isLoading: true },
        }));

        try {
            const response = await axios.get(
                `${BASE_API}/select/material-unit`,
                { params: { material_id: materialId } },
            );
            const units = response.data.data || [];

            setUnitsPerIndex((prev) => ({
                ...prev,
                [index]: { data: units, isLoading: false },
            }));

            if (units.length === 1) {
                const autoUnitId = String(units[0].value);
                setValue(
                    `items.${index}.material_unit_id`,
                    autoUnitId,
                );
                // Trigger balance check with auto-selected unit
                const currentItem = watchItems[index] || {};
                fetchBalance(index, {
                    ...currentItem,
                    material_id: materialId,
                    material_unit_id: autoUnitId,
                });
            }
        } catch (error) {
            console.error("Failed to fetch units:", error);
            setUnitsPerIndex((prev) => ({
                ...prev,
                [index]: { data: [], isLoading: false },
            }));
        }
    };

    const fetchBalance = async (index, item) => {
        if (
            !item?.warehouse_id ||
            !item?.material_id ||
            !item?.material_unit_id
        ) {
            setBalancesPerIndex((prev) => ({
                ...prev,
                [index]: { balance: null, isLoading: false },
            }));
            return null;
        }

        setBalancesPerIndex((prev) => ({
            ...prev,
            [index]: { balance: prev[index]?.balance ?? null, isLoading: true },
        }));

        try {
            const response = await axios.get(
                `${BASE_API}/stocks/check-material-balance`,
                {
                    params: {
                        warehouse_id: item.warehouse_id,
                        material_id: item.material_id,
                        material_unit_id: item.material_unit_id,
                    },
                },
            );
            const balance = Number(response.data.data?.balance || 0);
            setBalancesPerIndex((prev) => ({
                ...prev,
                [index]: { balance, isLoading: false },
            }));
            return balance;
        } catch (error) {
            console.error("Failed to fetch stock balance:", error);
            setBalancesPerIndex((prev) => ({
                ...prev,
                [index]: { balance: null, isLoading: false },
            }));
            return null;
        }
    };

    const getCurrentBalance = async (item) => {
        const response = await axios.get(
            `${BASE_API}/stocks/check-material-balance`,
            {
                params: {
                    warehouse_id: item.warehouse_id,
                    material_id: item.material_id,
                    material_unit_id: item.material_unit_id,
                },
            },
        );

        return Number(response.data.data?.balance || 0);
    };

    const handleMaterialChange = (index, key) => {
        setValue(`items.${index}.material_id`, key || "");
        setValue(`items.${index}.material_unit_id`, "");
        fetchUnitsForIndex(index, key);
        fetchBalance(index, {
            ...watchItems[index],
            material_id: key || "",
            material_unit_id: "",
        });
    };

    const handleUnitChange = (index, key) => {
        setValue(`items.${index}.material_unit_id`, key || "");
        fetchBalance(index, {
            ...watchItems[index],
            material_unit_id: key || "",
        });
    };

    const handleWarehouseChange = (index, key) => {
        setValue(`items.${index}.warehouse_id`, key || "");
        fetchBalance(index, {
            ...watchItems[index],
            warehouse_id: key || "",
        });
    };

    const handleAddRow = () => {
        append({ ...emptyItem });
    };

    const handleRemoveRow = (index) => {
        if (fields.length === 1) {
            showToast("warning", "Peringatan", "Minimal terisi satu item.");
            return;
        }

        remove(index);
        setUnitsPerIndex((prev) => {
            const newState = { ...prev };
            delete newState[index];
            return newState;
        });
        setBalancesPerIndex((prev) => {
            const newState = { ...prev };
            delete newState[index];
            return newState;
        });
    };

    const validateStock = async (items) => {
        const grouped = new Map();

        for (const item of items) {
            const key = [
                item.warehouse_id,
                item.material_id,
                item.material_unit_id,
            ].join("|");
            const current = grouped.get(key) || { item, qty: 0 };
            current.qty += Number(item.qty_out || 0);
            grouped.set(key, current);
        }

        for (const group of grouped.values()) {
            let balance = 0;

            try {
                balance = await getCurrentBalance(group.item);
            } catch (error) {
                console.error("Failed to fetch stock balance:", error);
                showToast(
                    "error",
                    "Gagal Mengecek Stok",
                    "Terjadi kesalahan saat mengecek stok tersedia.",
                );
                return false;
            }

            if (group.qty > balance) {
                showToast(
                    "error",
                    "Stok Tidak Cukup",
                    `Stok tersedia hanya ${balance}, sedangkan total qty keluar ${group.qty}.`,
                );
                return false;
            }
        }

        return true;
    };

    const handleStockOutSubmit = async (data) => {
        for (let i = 0; i < data.items.length; i++) {
            const item = data.items[i];
            if (!item.material_id) {
                showToast(
                    "error",
                    "Validasi Gagal",
                    `Produk pada baris ${i + 1} wajib dipilih.`,
                );
                return;
            }
            if (!item.warehouse_id) {
                showToast(
                    "error",
                    "Validasi Gagal",
                    `Gudang pada baris ${i + 1} wajib dipilih.`,
                );
                return;
            }
            if (!item.material_unit_id) {
                showToast(
                    "error",
                    "Validasi Gagal",
                    `Satuan pada baris ${i + 1} wajib dipilih.`,
                );
                return;
            }
            if (!item.date) {
                showToast(
                    "error",
                    "Validasi Gagal",
                    `Tanggal pada baris ${i + 1} wajib diisi.`,
                );
                return;
            }
            if (!item.qty_out || Number(item.qty_out) <= 0) {
                showToast(
                    "error",
                    "Validasi Gagal",
                    `Qty pada baris ${i + 1} wajib diisi dan lebih dari 0.`,
                );
                return;
            }
        }

        const isStockValid = await validateStock(data.items);
        if (!isStockValid) return;

        setPendingData(data);
        confirmModal.onOpen();
    };

    const handleConfirmSubmit = () => {
        if (!pendingData) return;

        const payload = {
            items: pendingData.items.map((item) => ({
                material_id: item.material_id,
                warehouse_id: item.warehouse_id,
                material_unit_id: item.material_unit_id,
                date: item.date,
                qty_out: Number(item.qty_out),
                description: item.description || null,
            })),
        };

        storeMutation.mutate(payload, {
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    "Stok keluar berhasil ditambahkan.",
                );
                navigate("/stock-out");
            },
            onError: (err) => {
                const message =
                    err.response?.status === 422
                        ? err.response?.data?.message ||
                          "Mohon periksa kembali input Anda. Pastikan stok tersedia mencukupi."
                        : "Terjadi kesalahan pada sistem. Silakan coba beberapa saat lagi.";

                showToast("error", "Gagal", message);
            },
        });
    };

    const getMaterialLabel = (id) => findOptionTitle(materials, id);
    const getWarehouseLabel = (id) => findOptionTitle(warehouses, id);
    const getUnitLabel = (index, id) =>
        findOptionTitle(unitsPerIndex[index]?.data, id);

    return (
        <div className="mx-auto flex max-w-6xl flex-col gap-6">
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title="Tambah Stok Keluar"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Stok Keluar", href: "/stock-out" },
                        { label: "Tambah" },
                    ]}
                />
            </div>

            <form onSubmit={handleSubmit(handleStockOutSubmit)}>
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/70 px-6 py-4">
                        <ThemedText className="font-semibold">
                            Daftar Produk Stok Keluar
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="flex flex-col gap-6 p-6">
                        {fields.map((field, index) => {
                            const item = watchItems[index] || {};
                            const balanceInfo = balancesPerIndex[index];

                            // Calculate effective balance by subtracting qty from other items with same product/warehouse/unit
                            const getEffectiveBalance = () => {
                                if (balanceInfo?.balance === null || balanceInfo?.balance === undefined) return null;
                                const key = [item.material_id, item.warehouse_id, item.material_unit_id].join("|");
                                let usedByOthers = 0;
                                watchItems.forEach((other, i) => {
                                    if (i === index) return;
                                    const otherKey = [other?.material_id, other?.warehouse_id, other?.material_unit_id].join("|");
                                    if (otherKey === key) {
                                        usedByOthers += Number(other?.qty_out || 0);
                                    }
                                });
                                return balanceInfo.balance - usedByOthers;
                            };
                            const effectiveBalance = getEffectiveBalance();

                            return (
                                <Card
                                    key={field.id}
                                    className="border-default-200 bg-default-50/50 border"
                                    shadow="none"
                                >
                                    <CardBody className="p-5">
                                        <div className="mb-5 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="bg-primary/10 text-primary flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold shadow-sm">
                                                    {index + 1}
                                                </div>
                                                <div>
                                                    <ThemedText className="text-default-900 font-semibold">
                                                        Item Stok Keluar
                                                    </ThemedText>
                                                    <ThemedText
                                                        size="xs"
                                                        className="text-default-400"
                                                    >
                                                        Isi detail stok barang
                                                    </ThemedText>
                                                </div>
                                            </div>
                                            <Button
                                                isIconOnly
                                                size="sm"
                                                variant="light"
                                                color="danger"
                                                className="opacity-70 hover:opacity-100"
                                                onPress={() =>
                                                    handleRemoveRow(index)
                                                }
                                            >
                                                <LuTrash2 size={18} />
                                            </Button>
                                        </div>

                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-6 xl:grid-cols-12">
                                            <Autocomplete
                                                label="Produk"
                                                placeholder="Cari & pilih produk..."
                                                isRequired
                                                isLoading={isLoadingMaterials}
                                                selectedKey={
                                                    item.material_id
                                                        ? String(
                                                              item.material_id,
                                                          )
                                                        : null
                                                }
                                                onSelectionChange={(key) =>
                                                    handleMaterialChange(
                                                        index,
                                                        key,
                                                    )
                                                }
                                                variant="bordered"
                                                isInvalid={
                                                    !!errors.items?.[index]
                                                        ?.material_id
                                                }
                                                errorMessage={
                                                    errors.items?.[index]
                                                        ?.material_id?.message
                                                }
                                                className="md:col-span-6 xl:col-span-6"
                                            >
                                                {(materials || []).map((m) => (
                                                    <AutocompleteItem
                                                        key={String(m.value)}
                                                        value={m.value}
                                                    >
                                                        {m.title}
                                                    </AutocompleteItem>
                                                ))}
                                            </Autocomplete>

                                            <Autocomplete
                                                label="Gudang"
                                                placeholder="Pilih lokasi"
                                                isRequired
                                                isLoading={isLoadingWarehouses}
                                                selectedKey={
                                                    item.warehouse_id
                                                        ? String(
                                                              item.warehouse_id,
                                                          )
                                                        : null
                                                }
                                                onSelectionChange={(key) =>
                                                    handleWarehouseChange(
                                                        index,
                                                        key,
                                                    )
                                                }
                                                variant="bordered"
                                                isInvalid={
                                                    !!errors.items?.[index]
                                                        ?.warehouse_id
                                                }
                                                errorMessage={
                                                    errors.items?.[index]
                                                        ?.warehouse_id?.message
                                                }
                                                className="md:col-span-3 xl:col-span-3"
                                            >
                                                {(warehouses || []).map((w) => (
                                                    <AutocompleteItem
                                                        key={String(w.value)}
                                                        value={w.value}
                                                    >
                                                        {w.title}
                                                    </AutocompleteItem>
                                                ))}
                                            </Autocomplete>

                                            <DatePicker
                                                label="Tanggal"
                                                isRequired
                                                showMonthAndYearPickers
                                                value={
                                                    item.date
                                                        ? parseDate(item.date)
                                                        : null
                                                }
                                                onChange={(date) =>
                                                    setValue(
                                                        `items.${index}.date`,
                                                        date
                                                            ? date.toString()
                                                            : "",
                                                    )
                                                }
                                                maxValue={today(
                                                    getLocalTimeZone(),
                                                )}
                                                variant="bordered"
                                                isInvalid={
                                                    !!errors.items?.[index]
                                                        ?.date
                                                }
                                                errorMessage={
                                                    errors.items?.[index]?.date
                                                        ?.message
                                                }
                                                className="md:col-span-3 xl:col-span-3"
                                            />

                                            <div className="flex flex-col gap-2 md:col-span-3 xl:col-span-6">
                                                <NumberInput
                                                    label="Qty Keluar"
                                                    placeholder="Contoh: 100"
                                                    isRequired
                                                    value={
                                                        item.qty_out ?? undefined
                                                    }
                                                    onValueChange={(val) =>
                                                        setValue(
                                                            `items.${index}.qty_out`,
                                                            val,
                                                        )
                                                    }
                                                    variant="bordered"
                                                    minValue={0}
                                                    hideStepper
                                                    isInvalid={
                                                        !!errors.items?.[index]
                                                            ?.qty_out
                                                    }
                                                    errorMessage={
                                                        errors.items?.[index]
                                                            ?.qty_out?.message
                                                    }
                                                    description={
                                                        balanceInfo?.isLoading
                                                            ? "Mengecek stok tersedia..."
                                                            : undefined
                                                    }
                                                />
                                                {!balanceInfo?.isLoading &&
                                                    effectiveBalance !== null && (
                                                        <Chip
                                                            size="sm"
                                                            variant="flat"
                                                            color={
                                                                effectiveBalance > 0
                                                                    ? "success"
                                                                    : "warning"
                                                            }
                                                            className="w-fit"
                                                        >
                                                            Stok tersedia:{" "}
                                                            {effectiveBalance}
                                                        </Chip>
                                                    )}
                                            </div>

                                            <Autocomplete
                                                label="Satuan"
                                                placeholder={
                                                    item.material_id
                                                        ? "Pilih satuan"
                                                        : "Pilih produk dulu"
                                                }
                                                isRequired
                                                isDisabled={!item.material_id}
                                                isLoading={
                                                    unitsPerIndex[index]
                                                        ?.isLoading
                                                }
                                                selectedKey={
                                                    item.material_unit_id
                                                        ? String(
                                                              item.material_unit_id,
                                                          )
                                                        : null
                                                }
                                                onSelectionChange={(key) =>
                                                    handleUnitChange(index, key)
                                                }
                                                variant="bordered"
                                                isInvalid={
                                                    !!errors.items?.[index]
                                                        ?.material_unit_id
                                                }
                                                className="md:col-span-3 xl:col-span-6"
                                            >
                                                {(
                                                    unitsPerIndex[index]
                                                        ?.data || []
                                                ).map((u) => (
                                                    <AutocompleteItem
                                                        key={String(u.value)}
                                                        value={u.value}
                                                    >
                                                        {u.title}
                                                    </AutocompleteItem>
                                                ))}
                                            </Autocomplete>

                                            <Textarea
                                                label="Keterangan"
                                                placeholder="Keterangan tambahan (opsional)"
                                                value={item.description || ""}
                                                onValueChange={(val) =>
                                                    setValue(
                                                        `items.${index}.description`,
                                                        val,
                                                    )
                                                }
                                                variant="bordered"
                                                className="md:col-span-6 xl:col-span-12"
                                            />
                                        </div>
                                    </CardBody>
                                </Card>
                            );
                        })}

                        <div>
                            <Button
                                type="button"
                                color="primary"
                                variant="flat"
                                size="sm"
                                startContent={<LuPlus size={16} />}
                                onPress={handleAddRow}
                            >
                                Tambah Item
                            </Button>
                        </div>
                    </CardBody>
                    <CardFooter className="flex justify-end gap-3 px-6 py-4">
                        <Button
                            as={Link}
                            href="/stock-out"
                            type="button"
                            variant="flat"
                            size="sm"
                        >
                            Batal
                        </Button>
                        <Button
                            color="primary"
                            onPress={handleSubmit(handleStockOutSubmit)}
                            isLoading={storeMutation.isLoading}
                            startContent={<LuSave size={16} />}
                            size="sm"
                        >
                            Simpan Stok
                        </Button>
                    </CardFooter>
                </Card>

                <Modal
                    isOpen={confirmModal.isOpen}
                    onOpenChange={confirmModal.onOpenChange}
                    size="3xl"
                    scrollBehavior="inside"
                    classNames={{
                        header: "border-b border-default-200",
                        footer: "border-t border-default-200",
                    }}
                >
                    <ModalContent>
                        {(onClose) => (
                            <>
                                <ModalHeader className="flex flex-col gap-1">
                                    <ThemedText>
                                        Konfirmasi Stok Keluar
                                    </ThemedText>
                                    <ThemedText
                                        variant="secondary"
                                        size="xs"
                                        className="font-normal"
                                    >
                                        Mohon periksa kembali data di bawah ini
                                        sebelum menyimpan.
                                    </ThemedText>
                                </ModalHeader>
                                <ModalBody className="p-0">
                                    <Table
                                        aria-label="Summary Table"
                                        removeWrapper
                                    >
                                        <TableHeader>
                                            <TableColumn>No</TableColumn>
                                            <TableColumn>
                                                Produk & Lokasi
                                            </TableColumn>
                                            <TableColumn>Qty</TableColumn>
                                        </TableHeader>
                                        <TableBody>
                                            {(pendingData?.items || []).map(
                                                (item, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>
                                                            {idx + 1}.
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-col">
                                                                <ThemedText
                                                                    className="font-semibold"
                                                                    size="sm"
                                                                >
                                                                    {getMaterialLabel(
                                                                        item.material_id,
                                                                    )}
                                                                </ThemedText>
                                                                <ThemedText
                                                                    variant="secondary"
                                                                    size="xs"
                                                                >
                                                                    {getWarehouseLabel(
                                                                        item.warehouse_id,
                                                                    )}
                                                                </ThemedText>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <ThemedText size="sm">
                                                                {item.qty_out}{" "}
                                                                {getUnitLabel(
                                                                    idx,
                                                                    item.material_unit_id,
                                                                )}
                                                            </ThemedText>
                                                        </TableCell>
                                                    </TableRow>
                                                ),
                                            )}
                                        </TableBody>
                                    </Table>
                                </ModalBody>
                                <ModalFooter>
                                    <Button
                                        variant="light"
                                        onPress={onClose}
                                        size="sm"
                                        className="text-default-500 hover:text-default-900 font-medium"
                                    >
                                        Batal
                                    </Button>
                                    <Button
                                        color="primary"
                                        onPress={handleConfirmSubmit}
                                        isLoading={storeMutation.isLoading}
                                        size="sm"
                                    >
                                        Konfirmasi Stok
                                    </Button>
                                </ModalFooter>
                            </>
                        )}
                    </ModalContent>
                </Modal>
            </form>
        </div>
    );
};

export default StockOutCreate;
